import type { TutorialStep } from "../constants/activationContent";

import * as React from "react";

import { PopoverPrimitive } from "@replo/design-system/components/popover/Popover";
import { AnimatePresence, motion } from "motion/react";

import { TutorialCard } from "./TutorialCard";

type TutorialPopoverProps = React.PropsWithChildren<{
  step: TutorialStep;
  /** 1-indexed */
  currentStep: number;
  totalSteps: number;
  isOpen: boolean;
  onNext: () => void;
  onSkip: () => void;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  isLastStep?: boolean;
  anchorRef?: React.RefObject<HTMLDivElement>;
  showStepIndicator?: boolean;
}>;

export function TutorialPopover({
  step,
  currentStep,
  totalSteps,
  isOpen,
  onNext,
  onSkip,
  side = "right",
  sideOffset = 16,
  align = "start",
  children,
  isLastStep = false,
  anchorRef,
  showStepIndicator = true,
}: TutorialPopoverProps) {
  return (
    <>
      <div ref={anchorRef} className="relative">
        {children}
      </div>

      <PopoverPrimitive.Root open={isOpen}>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner
            side={side}
            sideOffset={sideOffset}
            align={align}
            anchor={anchorRef}
            className="isolate z-50"
          >
            <PopoverPrimitive.Popup className="outline-hidden">
              <div className="hidden md:block">
                <TutorialCard
                  content={step}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  onNext={onNext}
                  onSkip={onSkip}
                  primaryButtonLabel={isLastStep ? "Finish" : "Next"}
                  showStepIndicator={showStepIndicator}
                  className="w-96"
                />
              </div>
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </>
  );
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TutorialOverlayProps {
  isVisible: boolean;
  onClick?: () => void;
  anchorRect?: AnchorRect | null;
}

const CUTOUT_PADDING = 8;
const BORDER_WIDTH = 2;

function getClipPath(anchorRect: AnchorRect | null | undefined): string {
  if (!anchorRect) {
    return "none";
  }
  const cutoutTop = anchorRect.top - CUTOUT_PADDING;
  const cutoutLeft = anchorRect.left - CUTOUT_PADDING;
  const cutoutRight = anchorRect.left + anchorRect.width + CUTOUT_PADDING;
  const cutoutBottom = anchorRect.top + anchorRect.height + CUTOUT_PADDING;

  return `polygon(
    0% 0%,
    0% 100%,
    ${cutoutLeft}px 100%,
    ${cutoutLeft}px ${cutoutTop}px,
    ${cutoutRight}px ${cutoutTop}px,
    ${cutoutRight}px ${cutoutBottom}px,
    ${cutoutLeft}px ${cutoutBottom}px,
    ${cutoutLeft}px 100%,
    100% 100%,
    100% 0%
  )`;
}

export function TutorialOverlay({
  isVisible,
  onClick,
  anchorRect,
}: TutorialOverlayProps) {
  const hasAnimated = React.useRef(false);

  // After first render with anchor, enable animations for subsequent steps
  React.useEffect(() => {
    if (anchorRect && !hasAnimated.current) {
      hasAnimated.current = true;
    }
  }, [anchorRect]);

  const clipPath = getClipPath(anchorRect);

  const borderTop = (anchorRect?.top ?? 0) - CUTOUT_PADDING - BORDER_WIDTH;
  const borderLeft = (anchorRect?.left ?? 0) - CUTOUT_PADDING - BORDER_WIDTH;
  const borderWidth =
    (anchorRect?.width ?? 0) + CUTOUT_PADDING * 2 + BORDER_WIDTH * 2;
  const borderHeight =
    (anchorRect?.height ?? 0) + CUTOUT_PADDING * 2 + BORDER_WIDTH * 2;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, clipPath }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 0.3 },
              clipPath: {
                duration: hasAnimated.current ? 0.3 : 0,
                ease: "easeOut",
              },
            }}
            className="fixed inset-0 z-40 bg-black/50 hidden md:block"
            onClick={onClick}
            aria-hidden="true"
          />
          {anchorRect && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                top: borderTop,
                left: borderLeft,
                width: borderWidth,
                height: borderHeight,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.3 },
                top: {
                  duration: hasAnimated.current ? 0.3 : 0,
                  ease: "easeOut",
                },
                left: {
                  duration: hasAnimated.current ? 0.3 : 0,
                  ease: "easeOut",
                },
                width: {
                  duration: hasAnimated.current ? 0.3 : 0,
                  ease: "easeOut",
                },
                height: {
                  duration: hasAnimated.current ? 0.3 : 0,
                  ease: "easeOut",
                },
              }}
              className="pointer-events-none fixed z-40 rounded-lg hidden md:block"
              aria-hidden="true"
            >
              {/* Gradient border using a pseudo-element technique with padding */}
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  padding: BORDER_WIDTH,
                  background:
                    "linear-gradient(135deg, #B7FEAE 18%, #FFB3C3 41%, #EF196E 69%, #8B32A8 100%)",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                }}
              />
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
